import { useMemo } from 'preact/hooks';
import CrayonLightMask from './CrayonLightMask';

// A still charcoal/crayon ground with weathered cuts of different sizes.

// Classical alchemical symbols, stroke only, in a 24-unit box.
const SIGILS = [
  'M12 4a8 8 0 1 0 0 16a8 8 0 1 0 0-16zM12 11a1 1 0 1 0 0 2a1 1 0 1 0 0-2z',            // gold, the sun
  'M15 4a8 8 0 1 0 0 16a6.5 6.5 0 1 1 0-16z',                                              // silver, the moon
  'M12 4l8 15H4z',                                                                          // fire
  'M12 20L4 5h16z',                                                                         // water
  'M12 4l8 15H4zM6.5 14h11',                                                                // air
  'M12 20L4 5h16zM6.5 11h11',                                                               // earth
  'M12 8a4 4 0 1 0 0 8a4 4 0 1 0 0-8zM12 16v6M9 19h6M7 3a5 5 0 0 0 10 0',                  // mercury
  'M12 4a8 8 0 1 0 0 16a8 8 0 1 0 0-16zM4 12h16',                                           // salt
  'M12 3l5 9H7zM12 12v9M8.5 17.5h7',                                                        // sulphur
  'M12 4a5 5 0 1 0 0 10a5 5 0 1 0 0-10zM12 14v7M8.5 18h7',                                  // antimony
  'M9 20a5 5 0 1 0 0-10a5 5 0 1 0 0 10zM13 11l7-7M15 4h5v5',                                // iron
  'M6 4h7M9.5 4v16M9.5 12h6a3 3 0 0 0 0-6',                                                 // tin
  'M6 6h8M10 6v14M10 12c4-2 7 0 5 3',                                                       // lead
  'M12 3v18M3 12h18M6.5 6.5l11 11M17.5 6.5l-11 11',                                         // the star of eight rays
  'M12 3v18M5 8l7 4l7-4M5 16l7-4l7 4',                                                      // arsenic
  'M12 4a8 8 0 1 0 0 16a8 8 0 1 0 0-16zM12 4v16M4 12h16',                                   // the wheel
];

// A deterministic scatter so the wall is the same every visit.
function mulberry(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function scatter() {
  const rnd = mulberry(29);
  const out = [];
  // Uneven spacing and a broad size range, with room around the larger cuts.
  for (let attempt = 0; attempt < 4000 && out.length < 280; attempt++) {
    const size = 16 + Math.pow(rnd(), 1.6) * 68;
    const x = rnd() * 1600;
    const y = rnd() * 900;
    if (out.some((g) => Math.hypot(x - g.x, y - g.y) < (size + g.s * 24) * 0.42 + 9)) continue;
    out.push({
      d: SIGILS[Math.floor(rnd() * SIGILS.length)],
      x, y, s: size / 24,
      r: rnd() * 80 - 40,
      o: 0.5 + rnd() * 0.5,
      width: 1.25 + rnd() * 0.65,
    });
  }
  return out;
}

export default function LaboratoryBackdrop({ waves = [] }) {
  const sigils = useMemo(scatter, []);
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 vh-lab-wall" />
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1600 900"
        preserveAspectRatio="xMidYMid slice"
        fill="none" strokeLinecap="round" strokeLinejoin="round"
      >
        <defs>
          {waves.length ? <CrayonLightMask id="vh-lab-rune-wax" width={1600} height={900} scale={0.55} /> : null}
          {/* One static texture pass across the wall: chipped edges and pitted cuts. */}
          <filter id="vh-lab-carved-stone" x="-5%" y="-5%" width="110%" height="110%" colorInterpolationFilters="sRGB">
            <feTurbulence type="fractalNoise" baseFrequency="0.14 0.28" numOctaves="2" seed="17" result="edgeNoise" />
            <feDisplacementMap in="SourceGraphic" in2="edgeNoise" scale="2.4" xChannelSelector="R" yChannelSelector="G" result="chipped" />
            <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="2" seed="31" result="stoneGrain" />
            <feColorMatrix in="stoneGrain" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0.8 0 0 0 0.35" result="pitting" />
            <feComposite in="chipped" in2="pitting" operator="in" />
          </filter>
          <filter id="vh-lab-rune-bloom" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.8" />
          </filter>
        </defs>
        <g filter="url(#vh-lab-carved-stone)" opacity="0.4">
          {sigils.map((g, i) => (
            <g key={i} opacity={g.o} transform={`translate(${g.x} ${g.y})`}>
              {/* A chipped lower bevel, dark recessed bed, and uneven inner face. */}
              <path d={g.d} stroke="#ad9876" strokeOpacity="0.24" strokeWidth={g.width + 0.6}
                transform={`translate(0.9 1.3) rotate(${g.r}) scale(${g.s}) translate(-12 -12)`} />
              <path d={g.d} stroke="#030201" strokeOpacity="0.88" strokeWidth={g.width + 0.35}
                transform={`rotate(${g.r}) scale(${g.s}) translate(-12 -12)`} />
              <path d={g.d} stroke="#514330" strokeOpacity="0.32" strokeWidth={g.width * 0.45}
                strokeDasharray="3.1 1.2 0.7 2.3" strokeDashoffset={i % 7}
                transform={`translate(0.2 0.4) rotate(${g.r}) scale(${g.s}) translate(-12 -12)`} />
            </g>
          ))}
        </g>
        {waves.map(brew => <g key={brew.id} data-testid="brew-rune-wave" mask="url(#vh-lab-rune-wax)" style={{ '--brew-color': brew.color }}>
          {sigils.map((g, i) => {
            const distance = Math.hypot(g.x - brew.x, g.y - brew.y);
            // A soft travelling front; the uneven cuts lend it their own rhythm.
            return <g key={i} transform={`translate(${g.x} ${g.y}) rotate(${g.r}) scale(${g.s}) translate(-12 -12)`}>
              <g className="vh-brew-rune" style={{ animationDelay: `${180 + distance * 1.05}ms`, '--rune-strength': (0.3 + g.o * 0.18) * Math.max(0.3, 1 - distance / 2200) }}>
                <path d={g.d} stroke={brew.color} strokeWidth={g.width + 1.3} opacity="0.45" filter="url(#vh-lab-rune-bloom)" />
                <g filter="url(#vh-lab-carved-stone)">
                <path d={g.d} stroke={brew.color} strokeWidth={g.width * 0.75} strokeOpacity="0.85" />
                <path d={g.d} stroke="#fff0ce" strokeWidth={g.width * 0.25} strokeDasharray="3.1 1.2 0.7 2.3" strokeDashoffset={i % 7} opacity="0.7" />
                </g>
              </g>
            </g>;
          })}
        </g>)}
      </svg>
    </div>
  );
}
